/**
=========================================================
* Rainman React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================
*/
import MDButton from "components/MDButton";
import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import axiosInstance from "utils/axios";

// Rainman React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Breadcrumbs from "examples/Breadcrumbs";

// Custom styles
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";



function DashboardNavbar({ absolute, light, isMini, refreshDashboard, userInfo }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;

  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  const handleCloseAllTrades = async () => {
    try {
      const response = await axiosInstance.post("/api/v1/alerts/close-all-positions");
      console.log("Close All Trades Response:", response.data);
      alert("All trades closed successfully.");
    } catch (error) {
      console.error("Error closing trades:", error);
      alert("Failed to close trades.");
    }
  };

  const handleResetMetrics = async () => {
    const confirmed = window.confirm("Are you sure you want to reset all metrics? This will start fresh from now.");
    if (!confirmed) return;

    try {
      const response = await axiosInstance.post("/api/v1/trades/reset-metrics", {
        reset_reason: "Manual reset from UI",
      });
      alert(`Metrics reset at ${response.data.reset_time}`);
      if (typeof refreshDashboard === "function") {
        refreshDashboard();
      }
    } catch (error) {
      console.error("Error resetting metrics:", error);
      alert("Failed to reset metrics.");
    }
  };

  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          color="inherit"
          mb={{ xs: 1, md: 0 }}
          sx={(theme) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
        </MDBox>

        {/* Profile Card Display */}
        {userInfo && (
          <MDBox
            color="inherit"
            mb={{ xs: 1, md: 0 }}
            sx={(theme) => navbarRow(theme, { isMini })}
          >
            <Card
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                px: 2,
                py: 0.75,
                mr: 2,
                background: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                  `linear-gradient(135deg, ${rgba(gradients.info.main, 0.1)}, ${rgba(gradients.info.state, 0.1)})`,
                backdropFilter: "blur(10px)",
                border: ({ functions: { rgba }, palette: { gradients } }) => `1px solid ${rgba(gradients.info.main, 0.2)}`,
                borderRadius: 2,
                boxShadow: ({ functions: { rgba }, palette: { gradients } }) => 
                  `0 4px 20px ${rgba(gradients.info.main, 0.15)}`,
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: ({ functions: { rgba }, palette: { gradients } }) => 
                    `0 8px 25px ${rgba(gradients.info.main, 0.25)}`,
                }
              }}
            >
              <MDBox display="flex" alignItems="center">
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  color={light ? "white" : "dark"}
                  sx={{ 
                    fontSize: "0.875rem",
                    mr: 1
                  }}
                >
                  {userInfo.username || "User"}
                </MDTypography>
                <MDBox
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    backgroundColor: "success.main",
                    animation: "pulse 2s infinite",
                    "@keyframes pulse": {
                      "0%": {
                        boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
                      },
                      "70%": {
                        boxShadow: "0 0 0 6px rgba(76, 175, 80, 0)",
                      },
                      "100%": {
                        boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
                      },
                    },
                  }}
                />
              </MDBox>
            </Card>
            
            {/* Mobile Profile Display */}
            <Card
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                px: 1.5,
                py: 0.5,
                mr: 2,
                background: ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                  `linear-gradient(135deg, ${rgba(gradients.info.main, 0.08)}, ${rgba(gradients.info.state, 0.08)})`,
                backdropFilter: "blur(8px)",
                border: ({ functions: { rgba }, palette: { gradients } }) => `1px solid ${rgba(gradients.info.main, 0.15)}`,
                borderRadius: 1.5,
                boxShadow: ({ functions: { rgba }, palette: { gradients } }) => 
                  `0 2px 12px ${rgba(gradients.info.main, 0.1)}`,
                transition: "all 0.2s ease-in-out",
              }}
            >
              <MDBox display="flex" alignItems="center">
                <MDTypography
                  variant="button"
                  fontWeight="medium"
                  color={light ? "white" : "dark"}
                  sx={{ 
                    fontSize: "0.75rem",
                    mr: 0.5
                  }}
                >
                  {userInfo.username || "User"}
                </MDTypography>
                <MDBox
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: "50%",
                    backgroundColor: "success.main",
                  }}
                />
              </MDBox>
            </Card>
          </MDBox>
        )}

        {!isMini && (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <Link to="/authentication/sign-in/basic">
                <IconButton sx={navbarIconButton} size="small" disableRipple>
                  <Icon sx={iconsStyle}></Icon>
                </IconButton>
              </Link>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={refreshDashboard}
              >
                <Icon sx={iconsStyle}>refresh</Icon>
              </IconButton>

              {/* Close All Positions Button */}
              <MDButton
                variant="gradient"
                color="error"
                size="small"
                onClick={handleCloseAllTrades}
                sx={{ ml: 2, mr: 1 }}
              >
                Close All Positions
              </MDButton>

              {/* Reset Metrics Button */}
              <MDButton
                variant="gradient"
                color="warning"
                size="small"
                onClick={handleResetMetrics}
                sx={{ mr: 2 }}
              >
                Reset Metrics
              </MDButton>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  refreshDashboard: PropTypes.func,
  userInfo: PropTypes.object,
};

export default DashboardNavbar;
